/* Pre-order header: the poster paints first; a background video loads after the page and fades in once it plays. */
if (!customElements.get('iah-preorder-hero')) {
  customElements.define('iah-preorder-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.media = this.querySelector('[data-hero-media]');
      this.toggle = this.querySelector('[data-video-toggle]');
      this.toggle?.addEventListener('click', () => this.setPaused(!this.paused), { signal });
      this.setupLayout(signal);
      this.setupCarousel(signal);
      if (!this.videoAllowed()) return;
      if (document.readyState === 'complete') setTimeout(() => this.start(), 0);
      else window.addEventListener('load', () => this.start(), { once: true, signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearInterval(this.pingTimer);
      clearTimeout(this.layoutTimer);
      clearTimeout(this.navTimer);
    }

    setupLayout(signal) {
      const update = () => { this.updateOffset(); this.fitText(); this.fitHeight(); this.placeArrows(); };
      window.addEventListener('resize', () => {
        clearTimeout(this.layoutTimer);
        this.layoutTimer = setTimeout(update, 120);
      }, { signal });
      window.addEventListener('load', update, { once: true, signal });
      document.fonts?.ready.then(() => { if (this.controller) update(); });
      update();
    }

    // Full height leaves room for whatever sits above the section (announcement bar, header).
    updateOffset() {
      if (!this.classList.contains('iah-ph--full') && !this.classList.contains('iah-ph--m-full')) return;
      const top = this.getBoundingClientRect().top + window.scrollY;
      if (top < window.innerHeight * 0.6) this.style.setProperty('--iah-ph-offset', `${Math.round(top)}px`);
      else this.style.removeProperty('--iah-ph-offset');
    }

    // Text set to break only between words shrinks when a single word (or a no-wrap line) is wider than its box.
    fitText() {
      this.querySelectorAll('[data-break="fit"], [data-break="nowrap"]').forEach((element) => {
        element.style.removeProperty('--fit');
        const width = element.clientWidth;
        if (!width || element.scrollWidth <= width + 1) return;
        element.style.setProperty('--fit', Math.max(0.4, Math.floor((width / element.scrollWidth) * 100) / 100));
      });
    }

    // Fixed heights: 'fill' grows the product images into the space the text leaves (up to the set maximum);
    // 'ratio' only shrinks them until everything fits. Never below a usable size.
    fitHeight() {
      this.style.removeProperty('--iah-ph-visual-max');
      this.style.removeProperty('--iah-ph-visual-h');
      const fill = this.classList.contains('iah-ph--size-fill');
      if (!fill && !this.classList.contains('iah-ph--size-ratio')) return;
      if (this.mobile && this.classList.contains('iah-ph--mobile-stack')) return;
      const visuals = [...this.querySelectorAll('.iah-ph__visual')];
      const inner = this.querySelector('.iah-ph__inner');
      if (!visuals.length || !inner) return;
      // 'Hoogte van de inhoud' leaves min-height at auto, so there is nothing to fit.
      const target = parseFloat(getComputedStyle(inner).minHeight);
      if (!target) return;
      const minimum = this.mobile ? 200 : 180;
      if (fill) {
        const maximum = parseFloat(getComputedStyle(visuals[0]).height);
        // Never taller than the most upright image can fill at the column's width; extra height would only add empty space above it.
        const usable = Math.max(...visuals.map((visual) => {
          const image = visual.querySelector('img');
          const ratio = image ? Number(image.getAttribute('height')) / Number(image.getAttribute('width')) : 0;
          return ratio ? visual.clientWidth * ratio : Infinity;
        }));
        this.style.setProperty('--iah-ph-visual-h', '0px');
        inner.style.minHeight = '0px';
        const available = Math.floor(target - inner.offsetHeight);
        inner.style.removeProperty('min-height');
        this.style.setProperty('--iah-ph-visual-h', `${Math.max(minimum, Math.min(maximum, usable, available))}px`);
        return;
      }
      const excess = inner.scrollHeight - target;
      if (excess <= 1) return;
      const tallest = Math.max(...visuals.map((visual) => visual.getBoundingClientRect().height));
      this.style.setProperty('--iah-ph-visual-max', `${Math.max(minimum, Math.floor(tallest - excess))}px`);
    }

    // Arrows on the image sit at the middle of the product picture.
    placeArrows() {
      const carousel = this.querySelector('.iah-ph__carousel');
      const visual = carousel?.querySelector('.iah-ph__visual');
      if (!carousel || !visual || !carousel.querySelector('.iah-ph__arrow')) return;
      const top = visual.getBoundingClientRect().top - carousel.getBoundingClientRect().top;
      carousel.style.setProperty('--iah-ph-arrow-y', `${Math.round(top + visual.offsetHeight / 2)}px`);
    }

    setupCarousel(signal) {
      const list = this.querySelector('[data-carousel]');
      if (!list || !this.querySelector('[data-carousel-nav]')) return;
      const slides = [...list.querySelectorAll('[data-slide]')];
      const dots = [...this.querySelectorAll('[data-dot]')];
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      const current = () => {
        const middle = list.scrollLeft + list.clientWidth / 2;
        let best = 0;
        slides.forEach((slide, index) => {
          const distance = (slide) => Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - middle);
          if (distance(slide) < distance(slides[best])) best = index;
        });
        return best;
      };
      const sync = () => {
        const index = current();
        dots.forEach((dot, n) => (n === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      const go = (index) => {
        const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        list.scrollTo({ left: slide.offsetLeft - (list.clientWidth - slide.offsetWidth) / 2, behavior: smooth ? 'smooth' : 'auto' });
      };
      list.addEventListener('scroll', () => {
        clearTimeout(this.navTimer);
        this.navTimer = setTimeout(sync, 60);
      }, { passive: true, signal });
      prev?.addEventListener('click', () => go(current() - 1), { signal });
      next?.addEventListener('click', () => go(current() + 1), { signal });
      dots.forEach((dot) => dot.addEventListener('click', () => go(Number(dot.dataset.dot)), { signal }));
      sync();
    }

    get mobile() { return window.matchMedia('(max-width: 749px)').matches; }

    videoAllowed() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (navigator.connection?.saveData) return false;
      if (this.mobile && this.dataset.videoMobile !== 'true') return false;
      return this.dataset.source !== 'none';
    }

    start() {
      if (!this.controller) return;
      if (this.dataset.source === 'youtube' && this.dataset.youtubeId) {
        if (this.dataset.consent !== 'true') return this.startYoutube();
        return this.whenConsented((granted) => (granted ? this.startYoutube() : this.startShopify()));
      }
      this.startShopify();
    }

    whenConsented(callback) {
      const { signal } = this.controller;
      document.addEventListener('visitorConsentCollected', (event) => {
        if (event.detail?.marketingAllowed && this.player?.kind !== 'youtube') {
          this.stopPlayer();
          this.startYoutube();
        }
      }, { signal });
      const decide = () => callback(window.Shopify?.customerPrivacy?.marketingAllowed?.() ?? false);
      if (window.Shopify?.designMode) return callback(true);
      if (window.Shopify?.customerPrivacy) return decide();
      if (!window.Shopify?.loadFeatures) return callback(false);
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => (error ? callback(false) : decide()));
    }

    pickSource(list) {
      if (!list.length) return null;
      const rect = this.media.getBoundingClientRect();
      const ratio = list[0].width / list[0].height || 16 / 9;
      const cap = this.mobile ? 1280 : 1920;
      const needed = Math.min(cap, Math.max(rect.width, rect.height * ratio) * Math.min(window.devicePixelRatio || 1, 2));
      const sorted = [...list].sort((a, b) => a.width - b.width);
      return sorted.find((source) => source.width >= needed) || sorted[sorted.length - 1];
    }

    startShopify() {
      const video = this.querySelector('video.iah-ph__video');
      if (!video) return;
      const parse = (value) => { try { return JSON.parse(value || '[]'); } catch { return []; } };
      const mobileSources = parse(video.dataset.mobileSources);
      const sources = this.mobile && mobileSources.length ? mobileSources : parse(video.dataset.sources);
      const source = this.pickSource(sources.length ? sources : mobileSources);
      if (!source) return;
      video.muted = true;
      video.src = source.url;
      video.addEventListener('playing', () => this.reveal(video), { once: true, signal: this.controller.signal });
      this.player = { kind: 'shopify', element: video, play: () => video.play().catch(() => {}), pause: () => video.pause() };
      this.player.play();
      this.watchViewport();
    }

    startYoutube() {
      if (this.player?.kind === 'youtube') return;
      const { signal } = this.controller;
      const id = this.dataset.youtubeId;
      const params = new URLSearchParams({
        autoplay: 1, mute: 1, loop: 1, playlist: id, controls: 0, playsinline: 1, rel: 0,
        modestbranding: 1, iv_load_policy: 3, disablekb: 1, enablejsapi: 1, origin: window.location.origin,
      });
      const frame = document.createElement('iframe');
      frame.className = 'iah-ph__video iah-ph__video--youtube';
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${params}`;
      frame.title = this.dataset.videoTitle || 'YouTube';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture';
      frame.tabIndex = -1;
      frame.setAttribute('aria-hidden', 'true');

      // The embed reports its state over postMessage; reveal only once it actually plays,
      // so a blocked autoplay never covers the poster with YouTube's play button.
      let heard = false;
      window.addEventListener('message', (event) => {
        if (event.source !== frame.contentWindow) return;
        let data = event.data;
        if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return; } }
        heard = true;
        const state = data?.event === 'onStateChange' ? data.info : data?.info?.playerState;
        if (state === 1) setTimeout(() => this.reveal(frame), 600);
      }, { signal });
      frame.addEventListener('load', () => {
        let tries = 0;
        const ping = () => frame.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: this.id || 'iah-preorder-hero', channel: 'widget' }), '*');
        ping();
        clearInterval(this.pingTimer);
        this.pingTimer = setInterval(() => {
          if (heard || ++tries > 12) {
            clearInterval(this.pingTimer);
            if (!heard) this.reveal(frame);
          } else ping();
        }, 500);
      }, { once: true, signal });

      const command = (func) => frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
      this.player = { kind: 'youtube', element: frame, play: () => command('playVideo'), pause: () => command('pauseVideo') };
      this.media.append(frame);
      this.watchViewport();
    }

    stopPlayer() {
      if (!this.player) return;
      this.player.pause();
      this.player.element.classList.remove('is-playing');
      if (this.player.kind === 'youtube') this.player.element.remove();
      this.player = null;
    }

    reveal(element) {
      if (this.player?.element !== element) return;
      element.classList.add('is-playing');
      if (this.toggle) this.toggle.hidden = false;
    }

    watchViewport() {
      if (this.observer) return;
      this.observer = new IntersectionObserver(([entry]) => {
        if (this.paused) return;
        if (entry.isIntersecting) this.player?.play();
        else this.player?.pause();
      });
      this.observer.observe(this);
    }

    setPaused(paused) {
      this.paused = paused;
      if (paused) this.player?.pause();
      else this.player?.play();
      if (!this.toggle) return;
      this.toggle.toggleAttribute('data-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    }
  });
}
